import { Button, Stack, Typography } from "@mui/material";
import { useState } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../api/adminApi";
import type { DwhShare } from "../../../api/types/integrationsTypes";
import useClientContext from "../../../context/useClientContext";
import AddShareDialog from "./AddShareDialog";
import DeleteShareDialog from "./DeleteShareDialog";
import DwhSharesTable from "./DwhSharesTable";
import UpdateShareDialog from "./UpdateShareDialog";

const DwhDataSharing = () => {
  const { hasPermissions } = useClientContext();
  const { sendNotification } = useNotificationContext();

  const [resp, fetchError, { fetch: fetchShares }] = useFetch(adminApi.getDwhShares);
  const [openAddShareDialog, setOpenAddShareDialog] = useState(false);
  const [editingShare, setEditingShare] = useState<DwhShare>();
  const [deletingShare, setDeletingShare] = useState<DwhShare>();

  if (fetchError) {
    logError(fetchError.message || "getDwhShares failed", "DwhDataSharing");
    return <DataLoadingFailed title="Loading DWH shares failed" />;
  }

  if (resp === undefined) {
    return <InlineLoader />;
  }

  const hasAccessToManage = hasPermissions(["ManageClientIntegrations"]);

  const handleShareButtonClick = () => {
    if (hasAccessToManage) {
      setOpenAddShareDialog(true);
    }
  };

  const handleEditClick = (share: DwhShare) => {
    if (hasAccessToManage) {
      setEditingShare(share);
    }
  };

  const handleDeleteClick = (share: DwhShare) => {
    if (hasAccessToManage) {
      setDeletingShare(share);
    }
  };

  const handleShareCreated = () => {
    setOpenAddShareDialog(false);
    sendNotification("Sharing created successfully");
    fetchShares();
  };

  const handleShareUpdated = async () => {
    setEditingShare(undefined);
    await fetchShares();
  };

  const handleShareDeleted = async () => {
    setDeletingShare(undefined);
    await fetchShares();
  };

  return (
    <>
      <Stack spacing={2.5}>
        <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
          <Stack>
            <Typography variant="subtitle1">Data Sharing</Typography>
            <Typography color="textSecondary">Share data hosted by Entrilia with another Snowflake account.</Typography>
          </Stack>
          {hasAccessToManage && (
            <Button variant="contained" onClick={handleShareButtonClick}>
              Share Data
            </Button>
          )}
        </Stack>
        <DwhSharesTable
          shares={resp.shares}
          readOnly={!hasAccessToManage}
          onEditClick={handleEditClick}
          onDeleteClick={handleDeleteClick}
        />
      </Stack>
      {openAddShareDialog && (
        <AddShareDialog onCancel={() => setOpenAddShareDialog(false)} onShareCreated={handleShareCreated} />
      )}
      {editingShare && (
        <UpdateShareDialog
          share={editingShare}
          onCancel={() => setEditingShare(undefined)}
          onShareUpdated={handleShareUpdated}
        />
      )}
      <DeleteShareDialog
        share={deletingShare}
        onCancel={() => setDeletingShare(undefined)}
        onShareDeleted={handleShareDeleted}
      />
    </>
  );
};

export default DwhDataSharing;
